import React, { useMemo } from 'react';
import {
  ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Radar, Tooltip,
} from 'recharts';
import { TRAIT_ORDER, TRAIT_META } from '../../../config/bigFiveMeta';
import { meanToPercent, bucketFromPercent } from './aggregateShared';

// Chart-plot area only gets an explicit height — never the whole card —
// so cards grow naturally on mobile (DS: no fixed height on mobile for
// content that isn't inside a fixed-alignment grid row). Both dimensions
// (w-full + explicit h-*) are set on the ResponsiveContainer's direct
// parent per the DS "Recharts ResponsiveContainer: Width Measurement
// Pitfall" rule, so iOS Safari / Android Chrome always get a real,
// non-zero measured width on first paint.
//
// Series legend is plain HTML below the plot (not Recharts <Legend>) so
// 4–5 department labels can wrap freely without clipping a fixed 30px
// legend band or stealing plot height on narrow phones.
const HEIGHT_CLASS = {
  hero: 'h-[280px] sm:h-[340px]',
  card: 'h-[210px] sm:h-[230px]',
};

/** HTML series key — "which line is which department" (separate from OCEAN colour key). */
const SeriesLegend = ({ series }) => (
  <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1.5" role="list" aria-label="Chart series">
    {series.map((s) => (
      <div key={s.id} className="inline-flex items-center gap-1.5 min-w-0 max-w-full" role="listitem">
        <span
          className={`w-4 flex-shrink-0 ${s.dashed ? 'border-t-2 border-dashed' : 'h-0.5 rounded-full'}`}
          style={s.dashed ? { borderColor: s.color } : { backgroundColor: s.color }}
          aria-hidden="true"
        />
        <span className="text-[11px] text-gray-600 truncate">{s.label}</span>
      </div>
    ))}
  </div>
);

/**
 * Custom PolarAngleAxis tick — renders each OCEAN axis in its
 * established trait colour (violet/emerald/orange/rose/slate — same
 * palette as `bigFiveMeta.js` and the employee-facing result page) so
 * HR can identify "which spoke is which" at a glance, with no legend
 * lookup required. Kept to the single-letter code (large + bold) rather
 * than a full trait name — SVG text wrapping/truncation on a ~300px
 * mobile radar is unreliable, whereas a single glyph never wraps. The
 * always-visible `OceanColorKey` (plain HTML, see aggregateShared.js)
 * is what spells out the full trait names legibly on every device.
 */
const ColoredAxisTick = ({ x, y, textAnchor, payload }) => {
  const meta = TRAIT_META[payload.value];
  if (!meta) return null;
  return (
    <text x={x} y={y} textAnchor={textAnchor} fill={meta.hex} fontSize={13} fontWeight={700}>
      {payload.value}
    </text>
  );
};

/**
 * OCEAN radar chart — the primary visualization for the Talent Insights
 * Aggregate dashboard. Plots one or more personality-profile "shapes"
 * (a company benchmark, department benchmarks) on the same five OCEAN
 * axes so HR sees the whole profile at a glance instead of five
 * disconnected bars.
 *
 * Trait identity (which axis is which) is always carried by axis
 * colour; series colour (which line belongs to whom) is separate and
 * only meaningful when more than one series is plotted.
 *
 * @param {Array<{id: string, label: string, color: string, dashed?: boolean, values: Object}>} series
 *   `values` is `{ O: meanRaw, C: meanRaw, ... }` on the 24..120 raw scale.
 * @param {'hero'|'card'} [size='hero']
 * @param {boolean} [showLegend]  Defaults to true once there is more
 *   than one series (the "which line is which department" legend).
 */
const OceanRadarChart = ({ series, size = 'hero', showLegend }) => {
  const chartData = useMemo(
    () => TRAIT_ORDER.map((code) => {
      const row = { code };
      series.forEach((s) => {
        const mean = s.values?.[code];
        row[s.id] = mean == null ? null : Math.round(meanToPercent(mean) * 10) / 10;
      });
      return row;
    }),
    [series],
  );

  const shouldShowLegend = showLegend ?? series.length > 1;

  return (
    <div className="w-full">
      <div className={`w-full ${HEIGHT_CLASS[size] || HEIGHT_CLASS.hero}`}>
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={chartData} margin={{ top: 8, right: 18, bottom: 8, left: 18 }}>
            <PolarGrid stroke="#e5e7eb" />
            <PolarAngleAxis dataKey="code" tick={<ColoredAxisTick />} />
            <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} tickCount={5} />
            {series.map((s) => (
              <Radar
                key={s.id}
                name={s.label}
                dataKey={s.id}
                stroke={s.color}
                fill={s.color}
                fillOpacity={s.dashed ? 0.05 : 0.16}
                strokeWidth={2}
                strokeDasharray={s.dashed ? '5 4' : undefined}
                dot={{ r: 3, strokeWidth: 0, fill: s.color }}
              />
            ))}
            <Tooltip
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '8px 10px',
                fontSize: '12px',
              }}
              labelFormatter={(label) => TRAIT_META[label]?.name || label}
              formatter={(value, name) => [bucketFromPercent(value).label, name]}
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>
      {shouldShowLegend && <SeriesLegend series={series} />}
    </div>
  );
};

export default OceanRadarChart;
